/**
 * Gestion › Pointer la garderie : la liste d'un jour d'une école, sur le
 * téléphone de l'administration ou de la coordination (02/11/2026).
 *
 * Le tableau de bord y mène en un geste : l'école, aujourd'hui, et la plage de
 * l'heure (le matin, la plage du matin ; l'après-midi, l'ATL). C'est l'écran du
 * lien des animateurs (`components/garderie/EcranDePointage.tsx`), nourri par
 * `garderie_semaine` et `garderie_pointer` : chaque ✓ est noté au compte de
 * qui le pose. Les autres écoles de garderie se choisissent en haut.
 */
import { useEffect, useMemo, useState } from 'react'
import { ArrowLeft, Loader2, School } from 'lucide-react'
import { EcranDePointage } from '../../../components/garderie/EcranDePointage'
import { useAuth } from '../../hooks/useAuth'
import type { NavigationParams } from '../../App'
import { lireLesEcoles, sourceDuBureau, type EcoleDeGarderie } from '../../../lib/garderie/presences-bureau.supabase'

interface Props {
  initialParams?: NavigationParams | null
  onRetour: () => void
}

export function PointageGarderie({ initialParams, onRetour }: Props) {
  const { userProfile } = useAuth()
  /* Les paramètres du tableau de bord servent à l'arrivée ; ensuite, on choisit ici. */
  const [depart] = useState(() => ({ centerId: initialParams?.centerId ?? '', date: initialParams?.date ?? null, plage: initialParams?.plage ?? null }))
  const [ecoles, setEcoles] = useState<EcoleDeGarderie[] | null>(null)
  const [ecoleId, setEcoleId] = useState(depart.centerId)
  const [erreur, setErreur] = useState<string | null>(null)

  useEffect(() => {
    lireLesEcoles()
      .then((e) => {
        setEcoles(e)
        setEcoleId((id) => (id && e.some((x) => x.centerId === id) ? id : e[0]?.centerId ?? ''))
      })
      .catch((e) => setErreur(e instanceof Error ? e.message : 'Impossible de lire les écoles'))
  }, [])

  const source = useMemo(() => (ecoleId ? sourceDuBureau(ecoleId) : null), [ecoleId])
  const surDepart = ecoleId === depart.centerId
  const qui = [userProfile?.prenom, userProfile?.nom].filter(Boolean).join(' ')

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={onRetour} className="flex items-center gap-1 rounded-lg px-2 py-1.5 text-sm text-gray-600 active:bg-gray-100">
          <ArrowLeft className="h-4 w-4" /> Tableau de bord
        </button>
        {ecoles && ecoles.length > 1 && (
          <div className="ml-auto inline-flex overflow-hidden rounded-lg border border-gray-300 text-sm">
            {ecoles.map((e, i) => (
              <button
                key={e.centerId}
                type="button"
                onClick={() => setEcoleId(e.centerId)}
                className={`flex items-center gap-1 px-3 py-1.5 ${i > 0 ? 'border-l border-gray-300' : ''} ${e.centerId === ecoleId ? 'bg-primary-600 text-white' : 'bg-white text-gray-700'}`}
              >
                <School className="h-4 w-4" /> {e.code || e.nom}
              </button>
            ))}
          </div>
        )}
      </div>

      {erreur && <p className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{erreur}</p>}
      {!erreur && ecoles === null && <Loader2 className="mx-auto mt-10 h-6 w-6 animate-spin text-gray-400" />}
      {ecoles !== null && ecoles.length === 0 && <p className="text-sm text-gray-500">Aucune école n’a de catalogue de garderie.</p>}

      {source && (
        <EcranDePointage
          key={ecoleId}
          source={source}
          plageInitiale={surDepart ? depart.plage : null}
          dateInitiale={surDepart ? depart.date : null}
          sousTitre={qui ? `Gestion · au nom de ${qui}` : 'Gestion'}
          pleinePage={false}
          hautCollant="top-14 sm:top-16"
        />
      )}
    </div>
  )
}
